<template>
  <div v-show="isAdvancedCalcSupported">
    <button
      :class="{
        [`${$options.$styles.toggler}`]: true,
        ' border-b -mb-px ': toolbarIsTop && isShowing,
        ' -mt-px border-t border-tint-less text-white ': isShowing,
      }"
      @click.prevent="toggleSettings"
      v-escape-key-func="close"
      data-test="settingsButton"
    >
      <icon
        aria-label="Settings"
        data-id="gear"
        class="fill-current max-md:-my-px"
      />
      <span class="hidden md:block">&#160;&#160;Settings</span>
    </button>
    <fader>
      <div
        v-show="isShowing"
        class="c-settings__pane"
        :class="{
          [`${$options.$styles.pane}`]: true,
          [`${$options.$styles.paneTop}`]: toolbarIsTop,
        }"
        :style="{ height: `calc(100vh - 3rem)` }"
      >
        <div
          :class="[
            $options.$styles.paneInner,
            toolbarIsTop
              ? ` -t-2px max-sm:t-full max-sm:-u-y-full `
              : ` t-full -u-y-full `,
          ]"
          style="line-height: 14px;"
        >
          <h2 class="u-beta mb-3 pt-2 -mt-px">Theme Colour</h2>
          <div data-test="themeControls">
            <labelled-range-control
              :class="$options.$styles.labelledRange"
              data-test="hueControl"
              v-model="theme.hue"
              max-value="360"
              @input="applyTheme"
              @change="saveSettings"
              >Hue</labelled-range-control
            >

            <labelled-range-control
              :class="$options.$styles.labelledRange"
              data-test="saturationControl"
              v-model="theme.saturation"
              max-value="2"
              @input="applyTheme"
              @change="saveSettings"
              >Saturation</labelled-range-control
            >

            <labelled-range-control
              :class="$options.$styles.labelledRange"
              data-test="lightnessControl"
              v-model="theme.lightness"
              min-value=".5"
              @input="applyTheme"
              @change="saveSettings"
              >Lightness</labelled-range-control
            >
          </div>

          <div>
            <h2 class="u-beta mb-3 pt-8 -mt-px whitespace-no-wrap">
              Contrast Mode
            </h2>
            <div class="justify-between -ml-1">
              <labelled-radio-input
                :checked="!contrastIsHigh"
                :class="{ 'text-white opacity-100': !contrastIsHigh }"
                @change="changeContrast"
                class="whitespace-no-wrap"
                value="normal"
                name="contrast"
              >
                Normal
              </labelled-radio-input>
              <labelled-radio-input
                :checked="contrastIsHigh"
                :class="{ 'text-white opacity-100': contrastIsHigh }"
                @change="changeContrast"
                class="ml-4 whitespace-no-wrap"
                value="high"
                name="contrast"
              >
                High
              </labelled-radio-input>
            </div>
          </div>

          <div class="max-sm:hidden">
            <h2 class="u-beta mb-3 pt-8 -mt-px whitespace-no-wrap">
              Toolbar Position
            </h2>
            <div class="justify-between -ml-1">
              <labelled-radio-input
                :checked="toolbarIsTop"
                :class="{ 'text-white opacity-100': toolbarIsTop }"
                @change="changeToolbar"
                class="whitespace-no-wrap"
                value="top"
                name="toolbar-position"
              >
                Top
              </labelled-radio-input>
              <labelled-radio-input
                :checked="!toolbarIsTop"
                :class="{ 'text-white opacity-100': !toolbarIsTop }"
                @change="changeToolbar"
                class="ml-4 whitespace-no-wrap"
                value="bottom"
                name="toolbar-position"
              >
                Bottom
              </labelled-radio-input>
            </div>
          </div>
        </div>
      </div>
    </fader>
  </div>
</template>

<script>
import LabelledRadioInput from './LabelledRadioInput'
import LabelledRangeControl from './LabelledRangeControl'
import { isAdvancedCalcSupported } from '../utilities/helpers'
import { mapActions, mapGetters } from 'vuex'
import Vue from 'vue'
import VueLocalStorage from 'vue-localstorage'
import EscapeKeyFunc from '@/directives/EscapeKeyFunc'

Vue.use(VueLocalStorage)

const $styles = {
  toggler: `
    border-bg-lightest
    flex
    items-center
    md:px-3
    max-md:border-r
    max-md:text-lg
    lg:px-2
    p-4
    select-none
    sm:hover:bg-bg
    sm:hover:text-white
    text-accent-60
    text-sm
    z-3
  `,
  pane: `
    absolute
    b-full
    b-full
    bg-dark-trans
    mb-px
    r
    sm:w-64
    u-items-pad-side
    w-48
    z-1
  `,
  paneTop: `
    mt-px
    sm:b-auto
    sm:mb-0
    sm:t-full
  `,
  paneInner: `
    c-settings__paneInner
    absolute
    u-transition-posi
    pb-8
    pt-7
    px-4
    md:py-7
    sm:py-8
    sm:px-8
    w-full
  `,
  labelledRange: `
    -mx-2 hover:bg-tint hover:opacity-100 opacity-60 px-2 py-1 relative rounded-md
  `,
}

export default {
  components: {
    LabelledRadioInput,
    LabelledRangeControl,
  },

  directives: {
    EscapeKeyFunc,
  },

  localStorage: {
    theme: {
      type: Object,
      default: {
        hue: 209,
        saturation: 1.4,
        lightness: 0.8,
      },
    },
  },

  data() {
    return {
      theme: Vue.localStorage.get(`theme`, {
        hue: 209,
        saturation: 1.4,
        lightness: 0.8,
      }),
    }
  },

  mounted() {
    this.applyTheme()
  },

  computed: {
    ...mapGetters(`settings`, [`isShowing`, `toolbarIsTop`, `contrastIsHigh`]),
    isAdvancedCalcSupported,
  },

  methods: {
    ...mapActions(`settings`, [`changeToolbar`, `changeContrast`, `toggle`]),
    ...mapActions(`settings`, {
      initializeToolbar: `setToolbar`,
      initializeContrast: `setContrast`,
    }),

    close(e) {
      if (this.isShowing) {
        this.toggleSettings()
        e.stopImmediatePropagation()
      }
    },

    toggleSettings() {
      if (!this.toggle()) this.$emit(`done`)
    },

    saveSettings() {
      Vue.localStorage.set(`theme`, this.theme)
    },

    applyTheme() {
      for (const color in this.theme) {
        document.documentElement.style.setProperty(
          `--${color}`,
          this.theme[color]
        )
      }
    },
  },

  created() {
    this.initializeToolbar()
    this.initializeContrast()
  },

  $styles,
}
</script>

<style lang="postcss" scoped>
@media (prefers-reduced-motion: no-preference) {
  .u-transition-posi {
    transition-property: top, transform;
    transition-duration: 0.6s;
    transition-delay: 0.3s;
  }
}
@responsive {
  .-u-y-full {
    transform: translateY(-100%);
  }
}
</style>
